/**
 * «Настройки → Цвета»: статусы заказов, сотрудники, «Финансы».
 *
 * Только для владельца — раздел даже не рисуется у сотрудника, а сервер
 * отклоняет чужое сохранение (модуль настроек закрыт для всех, кроме него).
 *
 * Правки здесь — ЧЕРНОВИК: программа вокруг не перекрашивается, пока не
 * нажато «Сохранить». Живой перекрас на каждый сдвиг ползунка означал бы,
 * что сотрудник за соседним компьютером этого не видит, а владелец видит
 * уже не то, что сохранено, — и два экрана расходятся без предупреждения.
 * Как будет выглядеть, показывает образец справа.
 */

import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import { AlertTriangle, Clock, RotateCcw } from 'lucide-react';
import {
  COLOR_PRESETS,
  DEFAULT_FINANCE_COLORS,
  DEFAULT_STATUS_COLORS,
  FINANCE_COLOR_KEYS,
  SIMILAR_COLOR_DISTANCE,
  STATUS_COLOR_KEYS,
  colorDistance,
  hexToHsv,
  hexToRgb,
  hsvToHex,
  normalizeHex,
  rgbToHex,
  textOnColor,
  type ColorSettings,
  type FinanceColorKey,
  type StatusColorKey,
} from '@otc/shared';

/** Всё, что правится на экране, — уже развёрнутыми цветами, без пропусков. */
export interface ColorDraft {
  status: Record<StatusColorKey, string>;
  finance: Record<FinanceColorKey, string>;
  employees: Record<string, string>;
}

/**
 * Черновик → то, что хранится: только отличия от стандартных.
 *
 * Сотрудники сохраняются ВСЕ: сохранение и есть момент, когда их цвет
 * закрепляется, — даже если никто его не трогал.
 */
export function colorDraftToSettings(draft: ColorDraft): ColorSettings {
  const status: ColorSettings['status'] = {};
  for (const key of STATUS_COLOR_KEYS) {
    if (draft.status[key] !== DEFAULT_STATUS_COLORS[key]) status[key] = draft.status[key];
  }
  const finance: ColorSettings['finance'] = {};
  for (const key of FINANCE_COLOR_KEYS) {
    if (draft.finance[key] !== DEFAULT_FINANCE_COLORS[key]) finance[key] = draft.finance[key];
  }
  return { status, finance, employees: { ...draft.employees } };
}

/** Сколько цветов черновика отличается от сохранённого. */
export function colorDraftChanges(draft: ColorDraft, saved: ColorDraft): number {
  let n = 0;
  for (const key of STATUS_COLOR_KEYS) if (draft.status[key] !== saved.status[key]) n += 1;
  for (const key of FINANCE_COLOR_KEYS) if (draft.finance[key] !== saved.finance[key]) n += 1;
  for (const id of Object.keys(draft.employees)) if (draft.employees[id] !== saved.employees[id]) n += 1;
  return n;
}

const STATUS_ROWS: { key: StatusColorKey; name: string; where: string }[] = [
  { key: 'done', name: 'Готово, оплачено', where: 'плашки «Готово» и «Оплачен», зелёные части обеих полос в сводке' },
  { key: 'normal', name: 'В очереди, в работе', where: 'плашки журнала, золотая часть полосы, кнопка «Срочные работы»' },
  { key: 'hold', name: 'Ожидание', where: 'замороженные заказы, синяя часть полосы' },
  { key: 'urgent', name: 'Срочно', where: 'плашка срочности' },
  { key: 'critical', name: 'Очень срочно, просрочка', where: 'плашка срочности, просроченная дата сдачи' },
  { key: 'danger', name: 'Не оплачен, ошибка', where: 'плашка «Не оплачен», красная часть полосы оплаты, ошибки' },
  { key: 'warning', name: 'Предупреждение', where: 'аванс, несохранённые правки, частичная оплата' },
];

const FINANCE_ROWS: { key: FinanceColorKey; name: string; where: string }[] = [
  { key: 'revenue', name: 'Выручка', where: 'строка расчёта, столбик на графике по месяцам' },
  { key: 'materials', name: 'Материалы', where: 'разбор месяца, столбик «Себестоимость»' },
  { key: 'rent', name: 'Аренда', where: 'разбор месяца' },
  { key: 'tax', name: 'Налог УСН', where: 'разбор месяца, налог в годовой таблице' },
  { key: 'other', name: 'Прочие расходы', where: 'разбор месяца' },
  { key: 'salaries', name: 'Зарплаты', where: 'разбор месяца' },
  { key: 'owner', name: 'Остаётся вам', where: 'разбор месяца, линия дохода на графике' },
];

type Tab = 'status' | 'employees' | 'finance';

/**
 * Вкладки. Описания под заголовком раздела нет — по решению владельца.
 * Короткое название — для телефона: «Статусы заказов» с соседями не
 * вставало в одну строку.
 */
const TABS: { id: Tab; label: string; short: string }[] = [
  { id: 'status', label: 'Статусы заказов', short: 'Статусы' },
  { id: 'employees', label: 'Сотрудники', short: 'Сотрудники' },
  { id: 'finance', label: 'Финансы', short: 'Финансы' },
];

export function ColorSettingsPanel({
  draft,
  saved,
  employees,
  onChange,
}: {
  draft: ColorDraft;
  saved: ColorDraft;
  /** Сотрудники, у которых есть цвет: техники с зарплатой, без владельца. */
  employees: { id: string; name: string; position?: string }[];
  onChange: (next: ColorDraft) => void;
}) {
  const [tab, setTab] = useState<Tab>('status');
  /** Строка, у которой раскрыт выбор цвета: одна за раз. */
  const [open, setOpen] = useState<string | null>(null);
  /*
   * Образец на телефоне — по кнопке. Он стоит ПОД строками, и развёрнутым
   * удлинял экран вдвое: чтобы дойти до почты, листали мимо таблицы-примера.
   * На широком экране он рядом со строками и места не отнимает.
   */
  const [preview, setPreview] = useState(false);
  const changes = colorDraftChanges(draft, saved);

  const setStatus = (key: StatusColorKey, hex: string) =>
    onChange({ ...draft, status: { ...draft.status, [key]: hex } });
  const setFinance = (key: FinanceColorKey, hex: string) =>
    onChange({ ...draft, finance: { ...draft.finance, [key]: hex } });
  const setEmployee = (id: string, hex: string) =>
    onChange({ ...draft, employees: { ...draft.employees, [id]: hex } });

  /*
   * «Вернуть все стандартные» — статусы и «Финансы». Сотрудников не трогает:
   * у них нет стандартного цвета, есть только закреплённый.
   */
  const resetAll = () =>
    onChange({ ...draft, status: { ...DEFAULT_STATUS_COLORS }, finance: { ...DEFAULT_FINANCE_COLORS } });

  const allDefault =
    STATUS_COLOR_KEYS.every((k) => draft.status[k] === DEFAULT_STATUS_COLORS[k]) &&
    FINANCE_COLOR_KEYS.every((k) => draft.finance[k] === DEFAULT_FINANCE_COLORS[k]);

  /** Похожий на правленый цвет статус — только для тех, что отличаются от стандартного. */
  const similarTo = (key: StatusColorKey): string | null => {
    const hex = draft.status[key];
    if (hex === DEFAULT_STATUS_COLORS[key]) return null;
    let best: { name: string; d: number } | null = null;
    for (const row of STATUS_ROWS) {
      if (row.key === key) continue;
      const d = colorDistance(hex, draft.status[row.key]);
      if (d < SIMILAR_COLOR_DISTANCE && (!best || d < best.d)) best = { name: row.name, d };
    }
    return best?.name ?? null;
  };

  return (
    <section
      className="mb-4 rounded-lg border p-3 md:mb-6 md:p-4"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-ink">Цвета</p>
        <button
          type="button"
          onClick={resetAll}
          disabled={allDefault}
          className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs text-ink2 hover:bg-black/5 disabled:opacity-40"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
        >
          <RotateCcw size={13} /> Вернуть все стандартные
        </button>
      </div>

      {/* Вкладки — как в «Конструкциях»: тёмная заливка у выбранной. */}
      <div
        className="mb-3 mt-2 grid grid-cols-3 gap-1 rounded-lg border p-1 sm:mb-4 sm:inline-flex"
        style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-bg)' }}
      >
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => {
              setTab(item.id);
              setOpen(null);
            }}
            className="whitespace-nowrap rounded-md px-2 py-1.5 text-xs font-medium sm:px-3"
            style={{
              background: tab === item.id ? 'var(--otc-accent-dark)' : 'transparent',
              color: tab === item.id ? '#fff' : 'var(--otc-ink2)',
            }}
          >
            <span className="sm:hidden">{item.short}</span>
            <span className="hidden sm:inline">{item.label}</span>
          </button>
        ))}
      </div>

      {/*
        Колонка — minmax(0, 1fr), а не просто одна: иначе сетка растягивалась
        под самую широкую таблицу-пример, и на узком телефоне вместе с ней
        уезжали за край строки цветов и кнопка ↺.
      */}
      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="overflow-hidden rounded-lg border" style={{ borderColor: 'var(--otc-line)' }}>
          {tab === 'status' &&
            STATUS_ROWS.map((row) => (
              <ColorRow
                key={row.key}
                id={`status.${row.key}`}
                name={row.name}
                where={row.where}
                value={draft.status[row.key]}
                reference={DEFAULT_STATUS_COLORS[row.key]}
                referenceLabel="стандартный"
                open={open === `status.${row.key}`}
                onOpen={setOpen}
                onChange={(hex) => setStatus(row.key, hex)}
                similar={similarTo(row.key)}
              />
            ))}
          {tab === 'finance' &&
            FINANCE_ROWS.map((row) => (
              <ColorRow
                key={row.key}
                id={`finance.${row.key}`}
                name={row.name}
                where={row.where}
                value={draft.finance[row.key]}
                reference={DEFAULT_FINANCE_COLORS[row.key]}
                referenceLabel="стандартный"
                open={open === `finance.${row.key}`}
                onOpen={setOpen}
                onChange={(hex) => setFinance(row.key, hex)}
              />
            ))}
          {tab === 'employees' &&
            (employees.length === 0 ? (
              <p className="p-4 text-sm text-ink2">Сотрудников с зарплатой пока нет.</p>
            ) : (
              employees.map((employee) => (
                <ColorRow
                  key={employee.id}
                  id={`employee.${employee.id}`}
                  name={employee.name}
                  where={`${employee.position ? `${employee.position.toLowerCase()} · ` : ''}точка, ползунок ставки, график выплат`}
                  value={draft.employees[employee.id] ?? '#52525B'}
                  /* У сотрудника нет стандартного цвета — возврат к сохранённому. */
                  reference={saved.employees[employee.id] ?? draft.employees[employee.id] ?? '#52525B'}
                  referenceLabel="сохранённый"
                  open={open === `employee.${employee.id}`}
                  onOpen={setOpen}
                  onChange={(hex) => setEmployee(employee.id, hex)}
                />
              ))
            ))}
        </div>

        <div>
          <button
            type="button"
            onClick={() => setPreview((v) => !v)}
            className="rounded-lg border px-2.5 py-1.5 text-xs text-ink2 xl:hidden"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
            aria-expanded={preview}
          >
            {preview ? 'Скрыть пример' : 'Показать, как будет выглядеть'}
          </button>
          <div className={preview ? 'mt-3 xl:mt-0' : 'hidden xl:block'}>
            <p className="mb-2 hidden text-[11px] font-medium uppercase tracking-wide text-ink2 xl:block">Как будет выглядеть</p>
            {tab === 'status' && <StatusPreview colors={draft.status} />}
            {tab === 'employees' && <EmployeesPreview employees={employees} colors={draft.employees} />}
            {tab === 'finance' && <FinancePreview colors={draft.finance} />}
          </div>
        </div>
      </div>

      <p className="mt-3 text-xs text-ink2">
        {changes > 0
          ? `Изменено цветов: ${changes}. У всех применится после «Сохранить» вверху.`
          : 'Изменений нет.'}
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Строка цвета
// ---------------------------------------------------------------------------

export function ColorRow({
  id,
  name,
  where,
  value,
  reference,
  referenceLabel,
  open,
  onOpen,
  onChange,
  similar,
}: {
  id: string;
  name: string;
  where: string;
  value: string;
  /** Куда ведёт ↺: стандартный цвет или сохранённый. */
  reference: string;
  referenceLabel: string;
  open: boolean;
  onOpen: (id: string | null) => void;
  onChange: (hex: string) => void;
  similar?: string | null;
}) {
  const changed = value !== reference;
  return (
    <div
      className="border-t px-3 py-3 first:border-t-0"
      style={{
        borderColor: 'var(--otc-line)',
        background: changed ? 'color-mix(in srgb, var(--otc-accent) 5%, var(--otc-surface))' : undefined,
      }}
    >
      <div className="flex items-center gap-x-2.5 gap-y-2 sm:flex-wrap sm:gap-x-3">
        <button
          type="button"
          onClick={() => onOpen(open ? null : id)}
          className="h-8 w-8 flex-shrink-0 rounded-lg border sm:h-10 sm:w-10"
          style={{
            background: value,
            borderColor: 'rgba(0,0,0,0.08)',
            boxShadow: open ? '0 0 0 2px var(--otc-surface), 0 0 0 4px var(--otc-accent)' : undefined,
          }}
          aria-label={`Выбрать цвет: ${name}`}
          aria-expanded={open}
        />
        <span className="min-w-0 flex-1 sm:min-w-[10rem]">
          {/* По слогам, а не посреди слога: «Ожидани-е» читалось как опечатка. */}
          <span className="block hyphens-auto text-[13px] font-medium text-ink sm:text-sm">
            {name}
            {changed && (
              <span
                className="ml-1.5 rounded px-1.5 py-px align-[2px] text-[10px] font-medium"
                style={{ color: 'var(--otc-accent)', background: 'color-mix(in srgb, var(--otc-accent) 12%, transparent)' }}
              >
                изменён
              </span>
            )}
          </span>
          <span className="block text-[11px] leading-snug text-ink2">{where}</span>
        </span>

        {/*
          HEX и RGB — одно значение, три способа. На телефоне в строке только
          HEX: три поля RGB рядом не помещались и уезжали на вторую-третью
          строку. Там они — внутри раскрытого выбора цвета.
        */}
        <span className="flex flex-shrink-0 items-center gap-1.5">
          <HexField value={value} onChange={onChange} />
          <RgbFields value={value} onChange={onChange} name={name} className="hidden sm:flex" />
          <button
            type="button"
            onClick={() => onChange(reference)}
            disabled={!changed}
            className="ml-0.5 grid h-8 w-8 place-items-center rounded-lg border text-ink disabled:border-transparent disabled:text-ink2 disabled:opacity-40"
            style={{ borderColor: 'var(--otc-line)' }}
            title={`Вернуть ${referenceLabel} ${reference}`}
            aria-label={`Вернуть ${referenceLabel} цвет`}
          >
            <RotateCcw size={14} />
          </button>
        </span>
      </div>

      {similar && (
        <p
          className="mt-2 flex items-start gap-2 rounded-lg border px-2.5 py-1.5 text-xs leading-snug"
          style={{
            color: 'var(--otc-amber)',
            background: 'color-mix(in srgb, var(--otc-amber) 8%, var(--otc-surface))',
            borderColor: 'color-mix(in srgb, var(--otc-amber) 25%, transparent)',
          }}
        >
          <AlertTriangle size={14} className="mt-px flex-shrink-0" />
          Новый цвет похож на «{similar}» — в журнале их различит только подпись на плашке.
          Сохранить можно, это подсказка.
        </p>
      )}

      {open && (
        <Picker
          name={name}
          value={value}
          reference={reference}
          referenceLabel={referenceLabel}
          onChange={onChange}
          onDone={() => onOpen(null)}
        />
      )}
    </div>
  );
}

/** Три числа R, G, B — то же значение, что HEX, другими словами. */
function RgbFields({
  value,
  onChange,
  name,
  className,
}: {
  value: string;
  onChange: (hex: string) => void;
  name: string;
  className: string;
}) {
  const rgb = hexToRgb(value);
  const setChannel = (channel: 'r' | 'g' | 'b', raw: string) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    onChange(rgbToHex(channel === 'r' ? n : rgb.r, channel === 'g' ? n : rgb.g, channel === 'b' ? n : rgb.b));
  };
  return (
    <span className={`items-center gap-1.5 ${className}`}>
      {(['r', 'g', 'b'] as const).map((channel) => (
        <label
          key={channel}
          className="flex items-center gap-1 rounded-lg border px-1.5 py-1"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
        >
          <span className="text-[10px] font-semibold uppercase text-ink2">{channel}</span>
          <input
            type="number"
            min={0}
            max={255}
            value={rgb[channel]}
            onChange={(e) => setChannel(channel, e.target.value)}
            className="tabular w-9 bg-transparent text-right text-xs text-ink outline-none"
            aria-label={`${name}: ${channel.toUpperCase()}`}
          />
        </label>
      ))}
    </span>
  );
}

/**
 * Поле HEX со своим черновиком.
 *
 * Код набирают по знаку, и на полпути он неполон: «#3A6» ещё не цвет. Такой
 * ввод подсвечивается, но не применяется — иначе цвет прыгал бы на каждой
 * букве, а стёртая решётка роняла бы значение в чёрный.
 */
function HexField({ value, onChange }: { value: string; onChange: (hex: string) => void }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const valid = normalizeHex(text) !== null;

  return (
    <label
      className="flex items-center gap-1 rounded-lg border px-2 py-1"
      style={{ borderColor: valid ? 'var(--otc-line)' : 'var(--otc-coral)', background: 'var(--otc-surface)' }}
    >
      {/* Подпись «HEX» — на широком экране; на телефоне код узнаётся по решётке. */}
      <span className="hidden text-[10px] font-semibold text-ink2 sm:inline">HEX</span>
      <input
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const hex = normalizeHex(e.target.value);
          if (hex) onChange(hex);
        }}
        /* Код обычно меняют целиком — вставкой или набором: выделяем сразу. */
        onFocus={(e) => e.currentTarget.select()}
        onBlur={() => setText(value)}
        maxLength={7}
        spellCheck={false}
        className="tabular w-[3.9rem] bg-transparent text-xs uppercase text-ink outline-none sm:w-[4.6rem]"
        aria-label="Код цвета HEX"
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Выбор цвета: квадрат, оттенок, готовые
// ---------------------------------------------------------------------------

/**
 * Выбор раскрывается ВНУТРИ строки, а не всплывающим окном.
 *
 * Всплывающее окно закрывало строки ниже — вместе с подсказкой «похож на…»,
 * ради которой цвет обычно и правят. В строке оно сдвигает остальное вниз и
 * ничего не прячет; на телефоне так же удобно, как на компьютере.
 */
function Picker({
  name,
  value,
  reference,
  referenceLabel,
  onChange,
  onDone,
}: {
  name: string;
  value: string;
  reference: string;
  referenceLabel: string;
  onChange: (hex: string) => void;
  onDone: () => void;
}) {
  /*
   * Оттенок хранится ОТДЕЛЬНО от кода цвета: у серого и чёрного его нет, и
   * без своей памяти полоса оттенка прыгала бы в красный, стоило увести
   * точку в угол квадрата.
   */
  const [hsv, setHsv] = useState(() => hexToHsv(value));
  const last = useRef(value);
  useEffect(() => {
    if (value !== last.current) {
      last.current = value;
      const next = hexToHsv(value);
      setHsv((prev) => (next.s === 0 || next.v === 0 ? { ...next, h: prev.h } : next));
    }
  }, [value]);

  const apply = (next: { h: number; s: number; v: number }) => {
    setHsv(next);
    const hex = hsvToHex(next.h, next.s, next.v);
    last.current = hex;
    onChange(hex);
  };

  const square = useDrag((x, y) => apply({ ...hsv, s: x, v: 1 - y }));
  const hueBar = useDrag((x) => apply({ ...hsv, h: x * 360 }));

  return (
    <div
      className="mt-3 grid gap-3 rounded-lg border p-3 sm:grid-cols-[minmax(0,260px)_1fr]"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      <div>
        <div
          {...square}
          className="relative h-32 cursor-crosshair touch-none rounded-md"
          style={{
            background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hsvToHex(hsv.h, 1, 1)})`,
          }}
        >
          <Knob style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, background: value }} />
        </div>
        <div
          {...hueBar}
          className="relative mt-3 h-3 cursor-pointer touch-none rounded-full"
          style={{ background: 'linear-gradient(90deg,#f00,#ff0,#0f0,#0ff,#00f,#f0f,#f00)' }}
        >
          <Knob style={{ left: `${(hsv.h / 360) * 100}%`, top: '50%', background: hsvToHex(hsv.h, 1, 1) }} />
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        {/* На телефоне числа RGB — здесь: в строке им не хватило места. */}
        <RgbFields value={value} onChange={onChange} name={name} className="flex sm:hidden" />
        <div className="grid grid-cols-8 gap-1.5">
          {COLOR_PRESETS.map((hex) => (
            <button
              key={hex}
              type="button"
              onClick={() => onChange(hex)}
              className="h-6 rounded-md border"
              style={{
                background: hex,
                borderColor: 'rgba(0,0,0,0.08)',
                boxShadow: hex === value ? '0 0 0 2px var(--otc-surface), 0 0 0 3px var(--otc-ink)' : undefined,
              }}
              title={hex}
              aria-label={`Готовый цвет ${hex}`}
            />
          ))}
        </div>
        <div className="mt-auto flex items-center justify-between gap-2 text-[11px] text-ink2">
          <span>
            {referenceLabel[0]!.toUpperCase() + referenceLabel.slice(1)} {reference} · готовые — в одно касание
          </span>
          <button
            type="button"
            onClick={onDone}
            className="rounded-lg border px-2.5 py-1 text-xs text-ink2 hover:bg-black/5"
            style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
          >
            Готово
          </button>
        </div>
      </div>
    </div>
  );
}

function Knob({ style }: { style: CSSProperties }) {
  return (
    <span
      className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white"
      style={{ boxShadow: '0 0 0 1px rgba(0,0,0,0.35)', ...style }}
    />
  );
}

/**
 * Перетаскивание по области: доли 0…1 по горизонтали и вертикали.
 *
 * Захват указателя — чтобы ползунок не терялся, когда палец или мышь
 * уходят за край квадрата: цвет упирается в край, а не замирает на полпути.
 */
function useDrag(onMove: (x: number, y: number) => void) {
  const handler = useRef(onMove);
  handler.current = onMove;
  const report = (event: PointerEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
    const y = Math.min(1, Math.max(0, (event.clientY - box.top) / box.height));
    handler.current(x, y);
  };
  return {
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
      event.currentTarget.setPointerCapture(event.pointerId);
      report(event);
    },
    onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) report(event);
    },
  };
}

// ---------------------------------------------------------------------------
// Образцы
// ---------------------------------------------------------------------------

const box = 'mb-3 rounded-lg border p-3';
const boxStyle: CSSProperties = { borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' };

function Pill({ text, color }: { text: string; color: string }) {
  return (
    <span
      className="inline-block whitespace-nowrap rounded-md px-1.5 py-0.5 text-[10px] font-semibold sm:px-2 sm:text-[11px]"
      style={{ background: color, color: textOnColor(color) }}
    >
      {text}
    </span>
  );
}

function Legend({ items }: { items: [string, string][] }) {
  return (
    <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-xs text-ink2">
      {items.map(([label, color]) => (
        <span key={label} className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />
          {label}
        </span>
      ))}
    </div>
  );
}

function StatusPreview({ colors }: { colors: Record<StatusColorKey, string> }) {
  const строки: [string, string, [string, string], [string, string] | null][] = [
    ['Суринов', '18-09', ['Очень срочно', colors.critical], ['Не оплачен', colors.danger]],
    ['Петров', '23-09', ['Срочно', colors.urgent], ['Не оплачен', colors.danger]],
    ['Лебедев', '25-09', ['В работе', colors.normal], null],
    ['Козлова', '—', ['Ожидание', colors.hold], null],
    ['Сидорова', '15-09', ['Готово', colors.done], ['Оплачен', colors.done]],
  ];
  return (
    <>
      <div className={box} style={boxStyle}>
        <div className="mb-2 flex gap-[3px]">
          <span className="h-3 rounded-full" style={{ flex: 5, background: colors.done }} />
          <span className="h-3 rounded-full" style={{ flex: 4, background: colors.normal }} />
          <span className="h-3 rounded-full" style={{ flex: 1, background: colors.hold }} />
        </div>
        <Legend items={[['Готово', colors.done], ['В очереди', colors.normal], ['Ожидание', colors.hold]]} />

        {/*
          ВТОРАЯ ПОЛОСА — оплата. В сводке заказов они стоят парой: слева
          работа, справа деньги, — и подбирать цвета по одной было гадание.
          Здесь видно сразу, не спорят ли зелёный с красным.
        */}
        <div className="mb-2 mt-3 flex gap-[3px]">
          <span className="h-3 rounded-full" style={{ flex: 2, background: colors.done }} />
          <span className="h-3 rounded-full" style={{ flex: 5, background: colors.danger }} />
        </div>
        <Legend items={[['Оплачено', colors.done], ['Задолженность', colors.danger]]} />

        {/* Кнопка «Срочные работы» — тем же цветом, что «В очереди»: срочное
            и есть та часть очереди, за которую берутся первой. */}
        <div className="mt-3">
          <span
            className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium"
            style={{
              borderColor: `color-mix(in srgb, ${colors.normal} 55%, transparent)`,
              background: `color-mix(in srgb, ${colors.normal} 14%, var(--otc-surface))`,
              color: colors.normal,
            }}
          >
            <Clock size={14} />
            Срочные работы
            <span
              className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-[11px] font-semibold text-white"
              style={{ background: colors.normal }}
            >
              3
            </span>
          </span>
        </div>
      </div>
      {/*
        На телефоне пример МЕНЬШЕ: мельче шрифт и поля, без столбца «Сдача».
        Цвет просроченной даты виден и по плашке «Очень срочно», а столбец
        вытеснял таблицу за край узкого экрана.
      */}
      <div className="mb-3 overflow-hidden rounded-lg border" style={boxStyle}>
        <table className="w-full text-[11px] sm:text-xs">
          <thead>
            <tr style={{ background: 'var(--otc-table-head)' }}>
              {['Пациент', 'Сдача', 'Статус', 'Оплата'].map((h) => (
                <th
                  key={h}
                  className={`px-1.5 py-1.5 text-left font-semibold text-white sm:px-2 ${h === 'Сдача' ? 'hidden sm:table-cell' : ''}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {строки.map(([пациент, дата, статус, оплата], i) => (
              <tr key={пациент} className="border-t" style={{ borderColor: 'var(--otc-line)' }}>
                <td className="px-1.5 py-1.5 text-ink sm:px-2">{пациент}</td>
                <td
                  className="tabular hidden px-1.5 py-1.5 sm:table-cell sm:px-2"
                  style={i === 0 ? { color: colors.critical, fontWeight: 600 } : { color: 'var(--otc-ink)' }}
                >
                  {дата}
                </td>
                <td className="px-1.5 py-1.5 sm:px-2"><Pill text={статус[0]} color={статус[1]} /></td>
                <td className="px-1.5 py-1.5 sm:px-2">{оплата ? <Pill text={оплата[0]} color={оплата[1]} /> : <span className="text-ink2">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={box} style={{ ...boxStyle, color: colors.warning }}>
        <span className="text-xs">⚠ Есть несохранённые правки · аванс выдан −25 000 ₽</span>
      </div>
    </>
  );
}

function EmployeesPreview({
  employees,
  colors,
}: {
  employees: { id: string; name: string }[];
  colors: Record<string, string>;
}) {
  /* Условные доли — только чтобы увидеть цвета рядом, не настоящие выплаты. */
  const столбики = [[1, 0.87], [0.94, 0.68], [1, 0.87], [0.45, 0.4]];
  const цвет = (id: string) => colors[id] ?? '#52525B';
  return (
    <>
      {employees.map((employee, i) => (
        <div key={employee.id} className={box} style={boxStyle}>
          <div className="mb-2 flex items-center gap-2">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: цвет(employee.id) }} />
            <span className="text-sm font-medium text-ink">{employee.name}</span>
          </div>
          <input
            type="range"
            min={0}
            max={50}
            value={i === 0 ? 9 : 8}
            readOnly
            tabIndex={-1}
            className="otc-range pointer-events-none"
            style={{ '--otc-fill-color': цвет(employee.id), '--otc-fill': `${((i === 0 ? 9 : 8) / 50) * 100}%` } as CSSProperties}
          />
        </div>
      ))}
      {employees.length > 0 && (
        <div className={box} style={boxStyle}>
          <p className="mb-2 text-[11px] uppercase tracking-wide text-ink2">Выплаты по месяцам · пример</p>
          <div className="flex h-28 items-end gap-3 border-b px-1" style={{ borderColor: 'var(--otc-line)' }}>
            {столбики.map((доли, i) => (
              <div key={i} className="flex flex-1 flex-col-reverse gap-[2px]">
                {employees.map((employee, j) => (
                  <span
                    key={employee.id}
                    className="block rounded-sm"
                    style={{ height: `${(доли[j % 2]! * 100) / Math.max(1, employees.length)}px`, background: цвет(employee.id) }}
                  />
                ))}
              </div>
            ))}
          </div>
          <div className="mt-2">
            <Legend items={employees.map((e) => [e.name, цвет(e.id)] as [string, string])} />
          </div>
        </div>
      )}
    </>
  );
}

function FinancePreview({ colors }: { colors: Record<FinanceColorKey, string> }) {
  /*
   * Доли по мотивам июля 2026 — только чтобы увидеть цвета рядом. Прочие
   * расходы в июле были нулём; здесь им дана доля, иначе их цвета не видно.
   */
  const части: [string, string, number][] = [
    ['Материалы', colors.materials, 21],
    ['Аренда', colors.rent, 5],
    ['Налог УСН', colors.tax, 5],
    ['Прочие', colors.other, 2],
    ['Зарплаты', colors.salaries, 13],
    ['Остаётся вам', colors.owner, 54],
  ];
  const выручка = [1.64, 1.89, 1.91, 1.81, 1.48];
  const материалы = [0.35, 0.4, 0.4, 0.38, 0.29];
  const доход = [0.9, 1.07, 1.08, 1.02, 0.84];
  return (
    <>
      <div className={box} style={boxStyle}>
        <p className="mb-2 text-[11px] uppercase tracking-wide text-ink2">Куда ушли деньги · пример</p>
        <div className="mb-2 flex gap-[3px]">
          {части.map(([name, color, доля]) => (
            <span key={name} className="h-3 rounded-full" style={{ flex: доля, background: color }} />
          ))}
        </div>
        <div className="space-y-1">
          {части.map(([name, color, доля]) => (
            <div key={name} className="flex items-center justify-between text-xs">
              <span className="inline-flex items-center gap-2 text-ink">
                <span className="inline-block h-2 w-2 rounded-sm" style={{ background: color }} />
                {name}
              </span>
              <span className="tabular text-ink2">{доля} %</span>
            </div>
          ))}
        </div>
      </div>
      <div className={box} style={boxStyle}>
        <p className="mb-2 text-[11px] uppercase tracking-wide text-ink2">По месяцам · пример</p>
        <svg viewBox="0 0 340 140" className="w-full" role="img" aria-label="Пример графика по месяцам">
          {выручка.map((в, i) => {
            const x = 18 + i * 64;
            return (
              <g key={i}>
                <rect x={x} y={122 - в * 58} width={22} height={в * 58} rx={3} fill={colors.revenue} />
                <rect x={x + 25} y={122 - материалы[i]! * 58} width={22} height={материалы[i]! * 58} rx={3} fill={colors.materials} />
              </g>
            );
          })}
          <polyline
            fill="none"
            stroke={colors.owner}
            strokeWidth={2.5}
            points={доход.map((д, i) => `${42 + i * 64},${122 - д * 58}`).join(' ')}
          />
          {доход.map((д, i) => (
            <circle key={i} cx={42 + i * 64} cy={122 - д * 58} r={4} fill={colors.owner} stroke="#fff" strokeWidth={2} />
          ))}
          <line x1={8} y1={122} x2={332} y2={122} stroke="var(--otc-line)" />
          {['апр', 'май', 'июн', 'июл', 'авг'].map((m, i) => (
            <text key={m} x={42 + i * 64} y={136} fontSize={10} textAnchor="middle" fill="var(--otc-ink2)">{m}</text>
          ))}
        </svg>
        <Legend items={[['Выручка', colors.revenue], ['Себестоимость', colors.materials], ['Доход', colors.owner]]} />
      </div>
    </>
  );
}
